import { format } from "date-fns";
import { Download } from "lucide-react";
import { useCertificates } from "../../../hooks/use-certificates";
import { Section } from "./section";

const formatLabels: Record<string, string> = {
  certificate: "Certificate",
  "certificate-extended": "Certificate Extended",
  calculations: "Calculations Printout",
  "report-residential": "Residential Report",
  "report-commercial": "Commercial Report",
  "report-mixed-use": "Mixed Use Report",
};

function formatLabel(value: string): string {
  return formatLabels[value] ?? value;
}

export function SectionCertificates({ recordId }: { recordId: string }): React.ReactElement {
  const { data, isLoading } = useCertificates(recordId);
  const certificates = data?.certificates ?? [];

  return (
    <Section id="certificates" title="Generated Certificates">
      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading certificates…</p>
      ) : certificates.length === 0 ? (
        <p className="text-sm text-muted-foreground">No certificates generated yet.</p>
      ) : (
        <div className="space-y-2" data-testid="certificates-list">
          {certificates.map((cert) => (
            <div
              key={cert.id}
              className="flex items-start justify-between gap-3 rounded-md border p-3"
              data-testid={`certificate-row-${cert.id}`}
            >
              <div className="min-w-0">
                <div className="text-sm font-medium">{formatLabel(cert.format)}</div>
                <div className="text-xs text-muted-foreground">
                  {format(new Date(cert.generatedAt), "d MMM yyyy, h:mm a")}
                  {cert.generatedBy ? ` · ${cert.generatedBy}` : ""}
                </div>
                {cert.notes ? <p className="mt-1 text-sm whitespace-pre-wrap">{cert.notes}</p> : null}
              </div>
              {cert.url ? (
                <a
                  href={cert.url}
                  className="flex shrink-0 items-center gap-1.5 text-sm font-medium text-primary hover:underline"
                  data-testid={`certificate-download-${cert.id}`}
                >
                  <Download className="h-3.5 w-3.5" />
                  Download
                </a>
              ) : (
                <span className="shrink-0 text-xs text-muted-foreground">No file stored</span>
              )}
            </div>
          ))}
        </div>
      )}
    </Section>
  );
}
